import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// Granular permissions for different areas of the app
export interface UserPermissions {
  canDeleteSessions: boolean
  canEditScopeHours: boolean
  canEditFormulas: boolean
  canEditProducts: boolean
  canEditRoadmap: boolean
  canViewAuditLog: boolean
  canEditJiraPrompts: boolean
  canEditSchemaOverrides: boolean
}

export interface User {
  email: string
  name: string
  picture?: string
  isAdmin: boolean
  permissions: UserPermissions
}

// Input type for setUser — isAdmin and permissions are computed, not provided by caller
type UserInput = Omit<User, 'isAdmin' | 'permissions'> & { isAdmin?: boolean; permissions?: UserPermissions }

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  devMode: boolean
  idToken: string | null  // Raw Google OAuth ID token for API auth

  // Actions
  setUser: (user: UserInput | null) => void
  setIdToken: (token: string | null) => void
  setLoading: (loading: boolean) => void
  setDevMode: (devMode: boolean) => void
  signOut: () => void
  isTokenExpired: () => boolean
}

const ALLOWED_DOMAIN = 'frankieone.com'
const ADMIN_USERS = [
  'paulo.goncalves@frankieone.com',
  'jacqui.tan@frankieone.com',
  'william.tong@frankieone.com',
  'pranav.vaprani@frankieone.com',
  'alitia.stpierre@frankieone.com',
  'aju.kurien@frankieone.com',
]

// Permission definitions per email
// If an email is not listed for a permission, they don't have it
const PERMISSION_GRANTS: Record<keyof UserPermissions, string[]> = {
  canDeleteSessions: [
    'paulo.goncalves@frankieone.com',
  ],
  canEditScopeHours: [
    'paulo.goncalves@frankieone.com',
    'alitia.stpierre@frankieone.com',
    'aju.kurien@frankieone.com',
    'shivendra.bhatia@frankieone.com',
    'boris.bujak@frankieone.com',
  ],
  canEditFormulas: [
    'paulo.goncalves@frankieone.com',
  ],
  canEditProducts: [
    'paulo.goncalves@frankieone.com',
    'jacqui.tan@frankieone.com',
    'william.tong@frankieone.com',
    'pranav.vaprani@frankieone.com',
    'kieran@frankieone.com',
    'alitia.stpierre@frankieone.com',
    'aju.kurien@frankieone.com',
  ],
  canEditRoadmap: [
    'paulo.goncalves@frankieone.com',
  ],
  canViewAuditLog: [
    'paulo.goncalves@frankieone.com',
    'jacqui.tan@frankieone.com',
    'william.tong@frankieone.com',
    'pranav.vaprani@frankieone.com',
  ],
  canEditJiraPrompts: [
    'paulo.goncalves@frankieone.com',
    'alitia.stpierre@frankieone.com',
  ],
  canEditSchemaOverrides: [
    'alitia.stpierre@frankieone.com',
  ],
}

function getPermissions(email: string): UserPermissions {
  return {
    canDeleteSessions: PERMISSION_GRANTS.canDeleteSessions.includes(email),
    canEditScopeHours: PERMISSION_GRANTS.canEditScopeHours.includes(email),
    canEditFormulas: PERMISSION_GRANTS.canEditFormulas.includes(email),
    canEditProducts: PERMISSION_GRANTS.canEditProducts.includes(email),
    canEditRoadmap: PERMISSION_GRANTS.canEditRoadmap.includes(email),
    canViewAuditLog: PERMISSION_GRANTS.canViewAuditLog.includes(email),
    canEditJiraPrompts: PERMISSION_GRANTS.canEditJiraPrompts.includes(email),
    canEditSchemaOverrides: PERMISSION_GRANTS.canEditSchemaOverrides.includes(email),
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      devMode: false,
      idToken: null,

      setIdToken: (token) => set({ idToken: token }),

      setUser: (user) => {
        if (user) {
          // Validate domain
          const emailDomain = user.email.split('@')[1]
          if (emailDomain !== ALLOWED_DOMAIN && !user.email.includes('localhost')) {
            console.error('Invalid email domain')
            set({ user: null, isAuthenticated: false, isLoading: false })
            return
          }

          // Check admin status and compute permissions
          const isAdmin = ADMIN_USERS.includes(user.email)
          const permissions = getPermissions(user.email)

          set({
            user: { ...user, isAdmin, permissions },
            isAuthenticated: true,
            isLoading: false,
          })
        } else {
          set({ user: null, isAuthenticated: false, isLoading: false })
        }
      },

      setLoading: (loading) => set({ isLoading: loading }),

      isTokenExpired: () => {
        const state = useAuthStore.getState()
        if (!state.idToken) return true
        try {
          const payload = JSON.parse(atob(state.idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
          return payload.exp < Math.floor(Date.now() / 1000) - 30
        } catch {
          return true
        }
      },

      setDevMode: (devMode) => {
        if (devMode) {
          set({
            devMode: true,
            user: {
              email: 'dev@frankieone.com',
              name: 'Dev User',
              isAdmin: true,
              permissions: {
                canDeleteSessions: true,
                canEditScopeHours: true,
                canEditFormulas: true,
                canEditProducts: true,
                canEditRoadmap: true,
                canViewAuditLog: true,
                canEditJiraPrompts: true,
                canEditSchemaOverrides: true,
              },
            },
            isAuthenticated: true,
            isLoading: false,
          })
        } else {
          set({ devMode: false })
        }
      },

      signOut: () => {
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          devMode: false,
          idToken: null,
        })
      },
    }),
    {
      name: 'frankieone-auth',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        devMode: state.devMode,
        idToken: state.idToken,
      }),
      // Re-compute permissions on rehydration so persisted users get up-to-date permissions
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<AuthState>
        const merged = { ...currentState, ...persisted }
        // Recompute permissions from email to ensure they're always current
        if (merged.user?.email) {
          merged.user = {
            ...merged.user,
            isAdmin: ADMIN_USERS.includes(merged.user.email),
            permissions: getPermissions(merged.user.email),
          }
        }
        return merged
      },
    }
  )
)
